let cartUpdateSequence = 0;
let cartUpdateRecentUntil = 0;
let celebratedSequence = -1;

const registerCartUpdate = () => {
  cartUpdateSequence += 1;
  cartUpdateRecentUntil = performance.now() + 5000;
};

document.addEventListener('cart:update', registerCartUpdate, { capture: true });
document.addEventListener('discount:update', registerCartUpdate, { capture: true });

class FreeShippingProgress extends HTMLElement {
  static observedAttributes = ['data-achieved'];

  #celebrating = false;
  #pendingSequence = null;
  #animationFrame = 0;
  #drawer = null;

  connectedCallback() {
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener('dialog:open', this.#handleDrawerOpen);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    const insertedDuringCartUpdate = performance.now() <= cartUpdateRecentUntil;
    if (insertedDuringCartUpdate && this.dataset.achieved === 'true') {
      this.#queueCelebration(cartUpdateSequence);
    }
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener('dialog:open', this.#handleDrawerOpen);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#drawer = null;

    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = 0;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-achieved' || oldValue === null || !this.isConnected) return;

    const crossedGoal = oldValue !== 'true' && newValue === 'true';
    if (crossedGoal) {
      this.#queueCelebration(cartUpdateSequence);
    } else if (newValue !== 'true') {
      this.#pendingSequence = null;
    }
  }

  #handleDrawerOpen = () => {
    if (this.#pendingSequence !== null && this.dataset.achieved === 'true') {
      this.#queueCelebration(this.#pendingSequence);
    }
  };

  #handleVisibilityChange = () => {
    if (
      document.visibilityState === 'visible' &&
      this.#pendingSequence !== null &&
      this.dataset.achieved === 'true'
    ) {
      this.#queueCelebration(this.#pendingSequence);
    }
  };

  #queueCelebration(sequence) {
    this.#pendingSequence = sequence;
    if (this.#animationFrame) return;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = requestAnimationFrame(() => {
        this.#animationFrame = 0;
        this.#celebrate();
      });
    });
  }

  async #celebrate() {
    const sequence = this.#pendingSequence;
    if (sequence === null) return;

    if (celebratedSequence === sequence || this.dataset.achieved !== 'true') {
      this.#pendingSequence = null;
      return;
    }

    if (
      this.dataset.confetti !== 'true' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      this.#pendingSequence = null;
      return;
    }

    if (this.#celebrating || document.visibilityState !== 'visible') return;
    if (this.getClientRects().length === 0 || this.clientWidth === 0) return;

    const container = this.querySelector('[data-confetti-container]');
    if (!(container instanceof HTMLElement) || typeof container.animate !== 'function') {
      this.#pendingSequence = null;
      return;
    }

    celebratedSequence = sequence;
    this.#pendingSequence = null;
    this.#celebrating = true;
    container.replaceChildren();

    const particleCount = 12;
    const spread = Math.min(this.clientWidth * 0.38, 132);
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < particleCount; index += 1) {
      const particle = document.createElement('i');
      particle.className = 'free-shipping-progress__confetti-particle';
      fragment.appendChild(particle);
    }

    container.appendChild(fragment);

    try {
      const animations = Array.from(container.children).map((particle, index) => {
        const isDrawer = this.classList.contains('free-shipping-progress--drawer');
        const direction = particleCount === 1 ? 0 : index / (particleCount - 1) - 0.5;
        const x = direction * spread * 2 + (Math.random() - 0.5) * 12;
        const apex = isDrawer ? -6 - Math.random() * 8 : -24 - Math.random() * 22;
        const landingY = isDrawer ? 24 + Math.random() * 24 : 18 + Math.random() * 22;
        const rotation = (Math.random() - 0.5) * 420;

        return particle.animate(
          [
            {
              opacity: 0,
              transform: 'translate(0, 0) rotate(0deg) scale(0.82)',
            },
            {
              opacity: 0.95,
              offset: 0.12,
              transform: `translate(${x * 0.18}px, -12px) rotate(${rotation * 0.12}deg) scale(1)`,
            },
            {
              opacity: 0.9,
              offset: 0.45,
              transform: `translate(${x * 0.62}px, ${apex}px) rotate(${rotation * 0.38}deg) scale(1)`,
            },
            {
              opacity: 0,
              transform: `translate(${x}px, ${landingY}px) rotate(${rotation}deg) scale(0.92)`,
            },
          ],
          {
            duration: 560 + Math.random() * 80,
            delay: index * 6,
            easing: 'linear',
            fill: 'forwards',
          }
        );
      });

      await Promise.allSettled(animations.map((animation) => animation.finished));
    } finally {
      container.replaceChildren();
      this.#celebrating = false;

      if (this.#pendingSequence !== null) {
        this.#queueCelebration(this.#pendingSequence);
      }
    }
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
